<template>
  <v-progress-linear
    v-if="!loaded"
    indeterminate
  />
  <div
    v-else
    class="d-flex flex-column ga-12"
    data-testid="dashboard-v2"
  >
    <div class="d-flex flex-column ga-4">
      <DashboardIntro
        @add-application="router.push({ name: 'ChildSelectOrCreate' })"
        :add-child="true"
        :disable-new-application="disableNewApplication"
        :first-name="store.state.profile.first_name"
        :hub-experience="hubExperience"
      />

      <DashboardInstructions :schema="groupSchema" />
    </div>

    <div class="d-flex flex-column ga-12">
      <DashboardTasks
        @reload-tasks="loadSummariesAndTasks"
        :tasks="applicationTasks"
      />

      <DashboardApplicationSummaries
        :items="applicationSummariesExcludingFormsAndWaitListRequests"
      />

      <DashboardFamilySubsidyPrograms
        v-if="!hubExperience"
        @create="createFamilySubsidy"
        :family-subsidy-programs="filteredFamilySubsidyPrograms"
        :processing="creatingSubsidy"
      />

      <EligibilityScreenerResult
        v-if="hubExperience"
        dashboard-view
      />
    </div>
  </div>
</template>

<script setup>
import DashboardApplicationSummaries from '@/parent/components/dashboards/DashboardApplicationSummaries.vue';
import DashboardIntro from '@/parent/components/dashboards/DashboardIntro.vue';
import DashboardInstructions from '@/parent/components/dashboards/DashboardInstructions.vue';
import DashboardTasks from '@/parent/components/dashboards/DashboardTasks.vue';
import DashboardFamilySubsidyPrograms from '@/parent/components/dashboards/DashboardFamilySubsidyPrograms.vue';
import EligibilityScreenerResult from '@/parent/views/eligibility-screener/EligibilityScreenerResult.vue';
import { subsidyProgramIsOpen } from '@/parent/services/subsidies';
import useApi from '@/shared/composables/useApi';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const { api } = useApi();
const router = useRouter();
const store = useStore();

const applicationSummaries = ref([]);
const familySubsidyPrograms = ref([]);
const subsidyPrograms = ref([]);
const familySubsidyProgramChildSubsidyProgramIds = ref([]);
const loaded = ref(false);
const hubExperience = ref(false);
const creatingSubsidy = ref(false);

defineProps({
  group: {
    type: Object,
    default: null,
  },
  groupSchema: {
    type: Object,
    default: null,
  },
});

// Our initial implementation of new enrollment forms should only show in-progress forms and only in the To Do section
const applicationSummariesExcludingFormsAndWaitListRequests = computed(() => {
  return applicationSummaries.value.filter((item) => !item.form_name && !item.wait_list_offer);
});

const applicationTasks = computed(() => {
  return applicationSummaries.value.filter((item) => ![null, 'Review'].includes(item.action));
});

const filteredFamilySubsidyPrograms = computed(() => {
  return familySubsidyPrograms.value.filter(displayFamilySubsidyProgram);
});

const getFamilySubsidyProgramApplications = (familySubsidyProgram) => {
  return applicationSummaries.value.filter(
    (application) => application.family_subsidy_program_id === familySubsidyProgram.id,
  );
};

const hasApplicationSummary = (familySubsidyProgram) => {
  return getFamilySubsidyProgramApplications(familySubsidyProgram).length > 0;
};

const hasApplicationSummaryInProgress = (familySubsidyProgram) => {
  return (
    getFamilySubsidyProgramApplications(familySubsidyProgram).filter(
      (application) => !application.submitted_at,
    ).length > 0
  );
};

const displayFamilySubsidyProgram = (familySubsidyProgram) => {
  const hasApplication = hasApplicationSummary(familySubsidyProgram);
  const hashApplicationInProgress = hasApplicationSummaryInProgress(familySubsidyProgram);
  const hasMultipleApplicationsEnabled =
    familySubsidyProgram.enable_multiple_applications &&
    (!hasApplication || !hashApplicationInProgress);

  return (
    subsidyProgramIsOpen(familySubsidyProgram) &&
    !familySubsidyProgram.enable_invite_only &&
    (hasMultipleApplicationsEnabled || !hasApplication)
  );
};

const disableNewApplication = computed(() => {
  const hasChildSubsidies = subsidyPrograms.value.length > 0;

  if (!hasChildSubsidies) return true;

  const allProgramsManaged = subsidyPrograms.value.every((subsidyProgram) =>
    familySubsidyProgramChildSubsidyProgramIds.value.includes(subsidyProgram.id),
  );

  return allProgramsManaged;
});

async function load() {
  loaded.value = false;

  await loadSummariesAndTasks();

  const resp = await api.public_api.organization.family_subsidy_program.index();
  familySubsidyPrograms.value = resp.data.map((familySubsidyProgram) => {
    if (familySubsidyProgram.subsidy_program_id) {
      familySubsidyProgramChildSubsidyProgramIds.value.push(
        familySubsidyProgram.subsidy_program_id,
      );
    }
    return {
      ...familySubsidyProgram,
      has_applied: Boolean(hasApplicationSummary(familySubsidyProgram)),
    };
  });

  const subsidyResp = await api.public_api.organization.subsidy_program.index();
  subsidyPrograms.value = subsidyResp.data;

  applicationSummaries.value = applicationSummaries.value.filter(shouldShowInviteOnlyFamilySubsidy);

  await setHubExperience();

  loaded.value = true;
}

async function loadSummariesAndTasks() {
  const response = await api.parent.applicationSummary.index();
  applicationSummaries.value = response?.data;
}

async function setHubExperience() {
  const response = await api.screenerProgram.index();
  const screenerPrograms = response?.data;

  hubExperience.value = screenerPrograms && screenerPrograms.length > 0;
}

const shouldShowInviteOnlyFamilySubsidy = (item) => {
  if (item.application_type !== 'FamilySubsidy') return true;

  const familySubsidyProgram = familySubsidyPrograms.value.find(
    (familySubsidyProgram) => familySubsidyProgram.id === item.family_subsidy_program_id,
  );

  if (!familySubsidyProgram?.enable_invite_only) return true;

  return !item.expired || item.submitted_at;
};

async function createFamilySubsidy(familySubsidyProgram) {
  creatingSubsidy.value = true;
  try {
    const resp = await api.parent.family_subsidy.create({
      family_subsidy_program_id: familySubsidyProgram.id,
    });
    router.push({ name: 'FamilySubsidyContinue', params: { id: resp.data.id } });
  } finally {
    creatingSubsidy.value = false;
  }
}

onMounted(load);
</script>
